import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from '../../../environments/environment';
import type { ApiEnvelope } from './super-admin-auth.service';
import type { ListModulePaginationMeta } from 'src/app/shared/components/list-module';

export interface EmailTemplateRow {
  id: number;
  uuid?: string | null;
  template_name: string | null;
  template_slug: string | null;
  subject: string | null;
  html_content: string | null;
  macro: string | null;
  module_name: string | null;
  is_active: boolean | null;
  created_by?: number | null;
  created_at?: string | null;
  updated_at?: string | null;
}

export interface EmailTemplateListPayload {
  result: EmailTemplateRow[];
  meta: ListModulePaginationMeta & { prev?: number | null; next?: number | null };
}

export interface EmailTemplateListQuery {
  page?: number;
  page_size?: number;
  searchText?: string;
  sortColumn?: string;
  sortBy?: 'asc' | 'desc';
  module?: string;
}

export interface UpdateEmailTemplateBody {
  template_name?: string;
  subject?: string;
  html_content?: string;
  macro?: string;
  module_name?: string;
  is_active?: boolean;
}

@Injectable({ providedIn: 'root' })
export class EmailTemplateApiService {
  private readonly http = inject(HttpClient);
  private readonly base = environment.apiBaseUrl.replace(/\/$/, '');

  listTemplates(
    query: EmailTemplateListQuery = {},
  ): Observable<ApiEnvelope<EmailTemplateListPayload>> {
    let params = new HttpParams();
    params = params.set('page', String(query.page ?? 1));
    params = params.set('page_size', String(query.page_size ?? 10));
    if (query.searchText?.trim()) {
      params = params.set('searchText', query.searchText.trim());
    }
    if (query.sortColumn) {
      params = params.set('sortColumn', query.sortColumn);
    }
    if (query.sortBy) {
      params = params.set('sortBy', query.sortBy);
    }
    if (query.module?.trim()) {
      params = params.set('module', query.module.trim());
    }
    return this.http.get<ApiEnvelope<EmailTemplateListPayload>>(
      `${this.base}/email-templates`,
      { params },
    );
  }

  getTemplateById(id: number): Observable<ApiEnvelope<EmailTemplateRow>> {
    return this.http.get<ApiEnvelope<EmailTemplateRow>>(
      `${this.base}/email-templates/${id}`,
    );
  }

  updateTemplate(
    id: number,
    body: UpdateEmailTemplateBody,
  ): Observable<ApiEnvelope<unknown>> {
    return this.http.patch<ApiEnvelope<unknown>>(
      `${this.base}/email-templates/update/${id}`,
      body,
    );
  }

  toggleTemplateStatus(
    id: number,
    is_active: boolean,
  ): Observable<ApiEnvelope<unknown>> {
    return this.http.patch<ApiEnvelope<unknown>>(
      `${this.base}/email-templates/toggle/${id}`,
      { is_active },
    );
  }

  sendTestMail(id: number): Observable<ApiEnvelope<unknown>> {
    return this.http.post<ApiEnvelope<unknown>>(
      `${this.base}/email-templates/${id}/test-mail`,
      {},
    );
  }
}
